import React, { useMemo } from 'react';

import { Divider } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { INutritionInfo } from '@rbi-ctg/menu';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  ItemNutritionInfoToHide,
  defaultItemNutritionInfoToHide,
} from 'state/launchdarkly/variations';
import { getUnitForNutrient } from 'utils/menu/units';

import { StyledAdditionalInfoIngredients } from '../styled/additional-information';
import { StyledAllergens } from '../styled/allergens';
import { ContentWrapper } from '../styled/content-wrapper';
import { ItemName } from '../styled/item-name';
import { Label } from '../styled/label';
import { Nutrient } from '../styled/nutrient';
import { Picture } from '../styled/picture';
import { TableHeader } from '../styled/table-header';
import { Nutrition } from '../v2/nutrition';

import { IItemNutritionProps } from './types';

export const ItemNutrition: React.FC<IItemNutritionProps> = ({
  allergenGroups,
  itemImage,
  itemName,
  nutritionInfo,
  enableFeatureNutritionIntegration,
  enableStaticMenuAllergens,
  additionalItemInformation,
}) => {
  const { formatNumber, formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const formatJoules = useFormatJoules();

  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const enableIngredients = useFlag(LaunchDarklyFlag.ENABLE_PRODUCT_INGREDIENTS);

  const itemsFromNutritionInfoToHide =
    useFlag<ItemNutritionInfoToHide>(LaunchDarklyFlag.HIDE_ITEMS_FROM_NUTRITION_INFO) ||
    defaultItemNutritionInfoToHide;

  const nutritionIds = useMemo(
    () =>
      enableEUNutritionDisplay
        ? ([
            'weight',
            'calories',
            'carbohydrates',
            'sugar',
            'fat',
            'saturatedFat',
            'proteins',
            'salt',
          ] as const)
        : ([
            'weight',
            'calories',
            'fat',
            'saturatedFat',
            'transFat',
            'cholesterol',
            'sodium',
            'carbohydrates',
            'fiber',
            'sugar',
            'proteins',
          ] as const),
    [enableEUNutritionDisplay]
  );

  const perServing = useMemo(() => {
    return (
      <>
        <TableHeader data-testid="per-serving-header">
          {formatMessage({ id: 'perServing' })}
        </TableHeader>
        {/* Todo: Remove both `any` casts once on Typescript v4 */}
        {(nutritionIds as any).map((key: keyof INutritionInfo) => {
          const value = nutritionInfo[key];

          if ((key === 'weight' && value === 0) || itemsFromNutritionInfoToHide[key]) {
            return null;
          }

          if (key === 'calories' && enableEUNutritionDisplay) {
            return (
              <Nutrient key={key}>
                <Label>{formatMessage({ id: `eu-${key}` })}</Label>
                <span>
                  {formatMessage(
                    { id: 'euNutritionValues' },
                    {
                      calories: formatCalories(value),
                      joules: formatJoules(value),
                    }
                  )}
                </span>
              </Nutrient>
            );
          }

          return (
            <Nutrient key={key}>
              <Label>
                {formatMessage({ id: (enableEUNutritionDisplay ? 'eu-' : 'us-') + key })}
              </Label>
              <span>
                {`${formatNumber(value, {
                  maximumFractionDigits: 1,
                })} ${getUnitForNutrient(key)}`}
              </span>
            </Nutrient>
          );
        })}
      </>
    );
  }, [
    formatMessage,
    nutritionIds,
    nutritionInfo,
    itemsFromNutritionInfoToHide,
    enableEUNutritionDisplay,
    formatNumber,
    formatCalories,
    formatJoules,
  ]);

  const perUnit = useMemo(() => {
    if (
      nutritionInfo.weight === 0 ||
      nutritionInfo.weight === undefined ||
      nutritionInfo.weight === null ||
      !enableEUNutritionDisplay
    ) {
      return null;
    }

    const createInnerContent = (key: keyof INutritionInfo) => {
      const weightRatio = nutritionInfo.weight / 100;

      if (key === 'weight') {
        return null;
      }

      const value = nutritionInfo[key] / weightRatio;

      if (key === 'calories' && enableEUNutritionDisplay) {
        return (
          <Nutrient key={key}>
            <Label>{formatMessage({ id: `eu-${key}` })}</Label>
            <span>
              {formatMessage(
                { id: 'euNutritionValues' },
                {
                  calories: formatCalories(value),
                  joules: formatJoules(value),
                }
              )}
            </span>
          </Nutrient>
        );
      }

      return (
        <Nutrient key={key}>
          <Label>{formatMessage({ id: (enableEUNutritionDisplay ? 'eu-' : 'us-') + key })}</Label>
          <span>
            {`${formatNumber(value, {
              maximumFractionDigits: 1,
            })} ${getUnitForNutrient(key)}`}
          </span>
        </Nutrient>
      );
    };

    /* Todo: Remove both `any` casts once on Typescript v4 */
    const content = (nutritionIds as any).map((key: keyof INutritionInfo) => {
      if (itemsFromNutritionInfoToHide[key]) {
        return null;
      }

      return createInnerContent(key);
    });

    return (
      <>
        <TableHeader data-testid="per-mass-header">
          {formatMessage({ id: 'perOneHundredGOrMl' })}
        </TableHeader>
        {content}
      </>
    );
  }, [
    nutritionInfo,
    enableEUNutritionDisplay,
    nutritionIds,
    itemsFromNutritionInfoToHide,
    formatMessage,
    formatNumber,
    formatCalories,
    formatJoules,
  ]);

  return (
    <>
      <Picture image={itemImage} alt="" objectFitContain />
      <ContentWrapper>
        <ItemName>{itemName}</ItemName>
        {enableFeatureNutritionIntegration ? (
          <Nutrition nutritionInfo={nutritionInfo} />
        ) : (
          <>
            {perServing}
            {perUnit}
          </>
        )}
        {additionalItemInformation ? (
          <>
            {enableIngredients && <Divider />}
            {enableIngredients && (
              <StyledAdditionalInfoIngredients additionalInfo={additionalItemInformation} />
            )}
            {enableIngredients && <Divider />}
          </>
        ) : null}
        {enableStaticMenuAllergens && !!allergenGroups?.length && (
          <>
            <TableHeader data-testid="allergens-header">
              {formatMessage({ id: 'allergenInformation' })}
            </TableHeader>
            <StyledAllergens allergenGroups={allergenGroups} />
          </>
        )}
      </ContentWrapper>
    </>
  );
};
